// The Feedback message catalog (Slice 6; docs/phase-0/slice6-ux-flows.md §5,
// docs/phase-0/slice6-component-designs.md §5). No copywriter draft file exists yet for
// Slice 6 (verified: docs/copy/ contains only slice4-certify-copy.md) - this mirrors
// apps/web/src/copy/money.ts's and apps/web/src/copy/pulse.ts's own established
// precedent exactly: every string the ux/component-design specs quote or shell as
// `[copy: flagged for review]` is reproduced here and rendered as DRAFT, never
// finalized by this build lane (CLAUDE.md §8 - legal/domain-sensitive copy is the
// copywriter's + counsel's lane, not this one's); every other key is a plain
// structural label (a button name, a heading, a field label), not a new claim, so it
// carries no separate flag, per money.ts's/pulse.ts's own bare-label convention.
//
// SCOPED, ENGLISH-ONLY, LOCAL TO apps/web - the same gap money.ts/pulse.ts name: no
// i18n-routed ICU MessageFormat mechanism exists in this repo yet (the
// i18n-engineer's lane, `i18n-localization`). `feedbackCopy()` below does plain
// `{token}` substitution only, no ICU plural rules.
//
// D45/D27 IS LOAD-BEARING HERE: the escalation copy states the record's own `reason`
// and `routedToLane` value, never a hardcoded lane or an invented threshold number
// (ux spec §5.5); no key below carries a cost/token/per-fix figure - Feedback surfaces
// no money figure of any kind (component-designs.md §5.1/§5.2/§5.3/§5.4's own D27
// checks, all "none").
export const FEEDBACK_COPY = {
  "feedback.tab.title": "Feedback",
  "feedback.loading.status": "Loading your feedback overview...",
  "feedback.loaded.status": "Feedback overview loaded.",
  "feedback.error.title": "We couldn't load your feedback overview.",
  "feedback.error.retryCta": "Try again",
  // [copy: flagged for review] - ux spec §5.6 empty-state shell, verbatim.
  "feedback.overview.empty": "No feedback yet for {appName}. Log what you already know below, or wait for your users.",

  // ---- Themes (ux spec §5.1; component-designs.md §5.1) ----
  "feedback.themes.heading": "Themes",
  "feedback.themes.empty": "Nothing clustered yet.",
  "feedback.themes.status.fixed": "Fixed",
  "feedback.themes.status.in_progress": "In progress",
  "feedback.themes.status.feature_queued": "Queued for the feature lane",
  "feedback.themes.status.watching": "Watching",
  "feedback.themes.status.praise": "Praise",
  "feedback.themes.sentiment.pos": "Positive",
  "feedback.themes.sentiment.neu": "Neutral",
  "feedback.themes.sentiment.neg": "Negative",
  "feedback.themes.sentiment.unknown": "Not enough ratings yet",
  "feedback.themes.linkedFix": "Linked to a shipped fix",
  "feedback.themes.escalatedChip": "Escalated",
  "feedback.themes.seeBeforeAfterCta": "See before/after",

  // ---- The closed loop (ux spec §5.3; component-designs.md §5.3) ----
  "feedback.closedLoop.heading": "Before and after the fix",
  "feedback.closedLoop.before": "Before the fix: {count} reports this theme, in the {window} before it shipped.",
  "feedback.closedLoop.after": "After: {count} reports since.",
  "feedback.closedLoop.loading": "Loading this theme's history...",
  "feedback.closedLoop.errorBody": "We couldn't load this theme's history. Try again.",
  "feedback.closedLoop.retryCta": "Try again",

  // ---- The D45 escalation (ux spec §5.5; component-designs.md §5.4) ----
  // [copy: flagged for review] - ux spec §5.5, verbatim shell, {lane} substitutes one
  // of the two lane keys below (never hardcoded to "shift").
  "feedback.escalation.banner": "{count} negative reports on {theme} this week - routed to {lane}.",
  "feedback.escalation.lane.shift": "tonight's shift",
  "feedback.escalation.lane.watch": "the watch",

  // ---- Top requests (ux spec §5.2; component-designs.md §5.2) ----
  "feedback.topRequests.heading": "Top requests",
  "feedback.topRequests.empty": "No requests yet.",
  "feedback.topRequests.soloUpsellBody": "Voting on requests is a Studio-and-up feature. You are on Solo.",
  "feedback.topRequests.askAboutUpgradingCta": "Ask about upgrading",
  "feedback.topRequests.voteCta": "Vote",
  "feedback.topRequests.votingCta": "Voting...",
  "feedback.topRequests.viewerDeniedReason": "Only an owner or admin can vote.",
  "feedback.topRequests.size.small": "Small",
  "feedback.topRequests.size.medium": "Medium",
  "feedback.topRequests.size.large": "Large",

  // ---- Founder-supplied feedback (ux spec §5.4; component-designs.md §5.5) ----
  "feedback.founderFeedback.heading": "Founder-supplied feedback",
  "feedback.founderFeedback.method.note": "Log a note",
  "feedback.founderFeedback.method.email": "Forward an email",
  "feedback.founderFeedback.method.import": "Import",
  // ASSUMPTION (flagged): the landed `FeedbackSubmitRequestBody.kind` is required
  // (`FeedbackKind`, enums.ts:215-216) and the ux spec's form names no selector for it
  // across its three founder entry methods - a founder-supplied note is not
  // structurally any ONE of the five widget-submission kinds. Rather than guess a
  // default that could misrepresent the entry, this catalog adds a plain selector;
  // flagged for the tech-lead/copywriter to confirm or replace with a fixed default.
  "feedback.founderFeedback.kindLabel": "What kind of feedback is this?",
  "feedback.founderFeedback.kind.bug": "A bug",
  "feedback.founderFeedback.kind.feature": "A feature idea",
  "feedback.founderFeedback.kind.vote": "A vote",
  "feedback.founderFeedback.kind.survey": "A survey response",
  "feedback.founderFeedback.kind.voice": "A voice note",
  "feedback.founderFeedback.note.fieldLabel": "What happened",
  "feedback.founderFeedback.email.fieldLabel": "What happened",
  "feedback.founderFeedback.email.subjectLabel": "Subject line (optional)",
  "feedback.founderFeedback.saveCta": "Save",
  "feedback.founderFeedback.savingCta": "Adding...",
  "feedback.founderFeedback.cancelCta": "Cancel",
  "feedback.founderFeedback.errorBody": "That didn't save. Try again.",
  // [copy: flagged for review] - ux spec §5.6, verbatim.
  "feedback.founderFeedback.savedToast": "Added. Themed alongside your other feedback.",
  "feedback.founderFeedback.viewerDeniedReason": "Only an owner or admin can add feedback.",
  "feedback.founderFeedback.listEmpty": "Nothing logged yet.",
  "feedback.founderFeedback.source.widget": "Widget",
  "feedback.founderFeedback.source.email": "Email",
  "feedback.founderFeedback.source.import": "Import",
  "feedback.founderFeedback.source.founder_note": "Note",

  // ---- The founder-feedback import panel (ux spec §5.4, the third hardened-upload
  // surface; component-designs.md §5.5) ----
  "feedback.import.heading": "Import feedback",
  "feedback.import.doneCta": "Done",
  // [copy: flagged for review] - the standing privacy notice (D77 pattern).
  "feedback.import.privacyNotice": "Files are scanned before your crew ever sees them. Nothing is shared outside your account.",
  "feedback.import.dropInstruction": "Drag a file here, or",
  "feedback.import.dragOver": "Drop it here",
  "feedback.import.chooseFileCta": "Choose a file",
  "feedback.import.wrongType": "That file didn't work. Use a {types} file under {size}.",
  "feedback.import.addCta": "Add",
  "feedback.import.uploading": "Adding {filename}...",
  "feedback.import.scanning": "Checking this file before your crew sees it...",
  "feedback.import.transferFailed": "That upload didn't go through. Try again.",
  "feedback.import.retrySameCta": "Try again",
  "feedback.import.retryCta": "Choose another file",
  // [copy: flagged for review] - the honest, no-internals-leaked three-way rejection
  // (identical discipline to slice5-ux-flows.md §5.2's own three rows).
  "feedback.import.rejectedMalware": "We couldn't add this file - it failed a security check.",
  "feedback.import.rejectedStructure": "We couldn't add this file - it isn't in a format we can read.",
  "feedback.import.rejectedContent": "We couldn't add this file - its content didn't pass our checks.",
  "feedback.import.accepted": "{filename} added. Themed alongside your other feedback.",
} as const;

export type FeedbackCopyKey = keyof typeof FEEDBACK_COPY;

/**
 * Plain `{token}` substitution only - no ICU plural rules (matching `moneyCopy`/
 * `pulseCopy`'s own established precedent exactly). A missing var leaves the literal
 * `{token}` in place rather than throwing, so a caller mistake is visible in the
 * render instead of a crash.
 */
export const feedbackCopy = (key: FeedbackCopyKey, vars?: Record<string, string | number>): string => {
  const template = FEEDBACK_COPY[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match: string, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
};
